import { Notifications } from '@api';
import { CircleLoader } from '@components/ui';
import { useApi } from '@data-access';
import { useOutsideAlerter } from '@hooks';
import { notificationsStore as importedNotificationsStore } from '@stores';
import { AppRoutes } from '@types';
import clsx from 'clsx';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { observer } from 'mobx-react';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

import styles from './NotificationsPopup.module.scss';

dayjs.extend(relativeTime);

interface NotificationsProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const NotificationsPopup: React.FC<NotificationsProps> = observer(({ isOpen, setIsOpen }) => {
  const { api } = useApi();
  const { push } = useRouter();

  const notificationsStore = importedNotificationsStore;

  // get last 10 all/unread notifications
  const fetchNotifications = useCallback(async () => {
    try {
      if (notificationsStore.notifications.length === 0) {
        const unreadResponse = await api.apiNotificationsList({ unread: true });
        // update number of unread notifications (count indicator)
        notificationsStore.setUnreadCount(unreadResponse.count);
        if (notificationsStore.showUnreadOnly) {
          notificationsStore.setNotifications(unreadResponse.results);
          notificationsStore.setNextPage(
            unreadResponse?.next ? parseInt(unreadResponse?.next.split('page=')[1], 10) : null,
          );
        } else {
          // get all notifications
          const response = await api.apiNotificationsList();
          notificationsStore.setNotifications(response.results);
          notificationsStore.setNextPage(response?.next ? parseInt(response?.next.split('page=')[1], 10) : null);
        }
      }
    } catch (error) {
      console.log(error);
    }
  }, [api, notificationsStore]);

  const loadMore = useCallback(async () => {
    try {
      const response = await api.apiNotificationsList({
        page: notificationsStore.nextPage,
        unread: notificationsStore.showUnreadOnly ? true : undefined,
      });
      notificationsStore.addNotifications(response.results);
      if (response?.next) {
        notificationsStore.setNextPage(parseInt(response?.next.split('page=')[1], 10));
      } else {
        notificationsStore.setNextPage(null);
      }
    } catch (error) {
      console.log(error);
    }
  }, [api, notificationsStore]);

  const toggleUnread = async () => {
    const responseUnread = await api.apiNotificationsList({
      unread: notificationsStore.showUnreadOnly ? undefined : true,
    });
    notificationsStore.setNotifications(responseUnread.results);
    notificationsStore.setNextPage(responseUnread?.next ? parseInt(responseUnread?.next.split('page=')[1], 10) : null);
    notificationsStore.toggleUnreadOnly();
  };

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // hide on outside click
  const notificationsRef = useRef(null);
  useOutsideAlerter(notificationsRef, e => {
    if (!(e.target as HTMLElement).hasAttribute('data-notification')) {
      setIsOpen(false);
    }
  });

  const handleNotificationClick = async (notification: Notifications) => {
    if (notification.unread) {
      // if notification is unread set to be read
      await api.apiNotificationsMarkAsReadCreate({
        id: notification.id,
      });
      notificationsStore.setNotifications(
        notificationsStore.notifications.map(n => (n.id === notification.id ? { ...n, unread: false } : n)),
      );
      notificationsStore.setUnreadCount(notificationsStore.unreadNotificationsCount - 1);
    }
    if ((notification.type as string) === `NotificationType.ASSESSMENT`) {
      push(`${AppRoutes.assessmentInventory}/${notification.sender}`);
    } else if ((notification.type as string) === `NotificationType.MUTATION`) {
      if (notification.text.includes('model')) {
        push(`${AppRoutes.modelDetails}/${notification.sender}`);
      }
      if (notification.text.includes('Use case')) {
        push(`${AppRoutes.useCaseDetails}/${notification.sender}`);
      }
    }
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div
      className={styles.popup}
      ref={notificationsRef}
      id="scrollableDiv"
    >
      <div className={styles.header}>
        <div className={styles.label}>Notifications</div>
        <div className={styles.switchWrapper}>
          <span className={styles.unreadLabel}>Only show unread</span>
          <label className={styles.switch}>
            <input
              type="checkbox"
              checked={notificationsStore.showUnreadOnly}
              onChange={() => {
                toggleUnread();
              }}
            />
            <span className={clsx(styles.slider, styles.round)} />
          </label>
        </div>
      </div>
      {notificationsStore.unreadNotificationsCount > 0 && (
        <div
          onClick={() => notificationsStore.markAllAsRead(api)}
          className={styles.markRead}
        >
          Mark all as read
        </div>
      )}
      <div className={styles.notifications}>
        <InfiniteScroll
          className={styles.notifications}
          dataLength={notificationsStore.notifications.length} // This is important field to render the next data
          next={() => loadMore()}
          hasMore={!!notificationsStore.nextPage}
          loader={
            <CircleLoader
              className={styles.loaderWrapper}
              classNameLoader={styles.loader}
            />
          }
          scrollableTarget="scrollableDiv"
        >
          {notificationsStore.notifications.length > 0 ? (
            notificationsStore.notifications.map(notification => (
              <div
                className={styles.notification}
                onClick={() => handleNotificationClick(notification)}
                key={notification.id}
              >
                <div>
                  <div className={styles.text}>{notification.text}</div>
                  <div className={styles.timestamp}>{dayjs().to(notification.timestamp)}</div>
                </div>
                {notification.unread && <div className={styles.unreadIndicator} />}
              </div>
            ))
          ) : (
            <div className={styles.emptyMessage}>
              {notificationsStore.showUnreadOnly
                ? 'You have no unread notifications.'
                : 'You have no notifications yet.'}
            </div>
          )}
        </InfiniteScroll>
      </div>
    </div>
  );
});
